/**
 * RTWO Asset & Token Engine — Permission Service
 */

import type { AssetId, PermissionId, RTWOAssetPermission } from '../domain-models';
import type { IPermissionService, PermissionResult } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type { CryptographyAdapterRegistry } from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class PermissionService extends BaseASSService implements IPermissionService {
  readonly serviceId = 'ass-permission-service-001';
  readonly serviceName = 'Permission Service';
  readonly description =
    'Permission service — placeholder, requires real Permission Backend. No fake permissions.';
  readonly integrations = ['Core Engine', 'Cryptography Engine', 'Wallet Engine', 'Ledger Engine'];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = false;

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly cryptographyRegistry: CryptographyAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async grantPermission(
    assetId: AssetId,
    permissionType: string,
    subjectReference: string
  ): Promise<PermissionResult> {
    if (!this.cryptographyRegistry.isConfigured()) {
      return {
        success: false,
        permissionId: null,
        error: 'Permission granting requires real Permission Backend — Not Configured',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Permission Backend Not Configured',
      };
    }

    const adapterResult = await this.cryptographyRegistry
      .getActiveAdapter()
      .grantPermission(assetId, permissionType, subjectReference);

    if (adapterResult?.permission) {
      this.repositories.permissions.save(adapterResult.permission);
      this.context.eventBus.emit(ASSTInternalEventTypes.PERMISSION_GRANTED, {
        permissionId: adapterResult.permission.permissionId,
        assetId,
      });
    }

    return {
      success: false,
      permissionId: null,
      error: 'Permission granting requires real Permission Backend — Not Configured',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Permission Backend Not Configured',
    };
  }

  getAllPermissions(): RTWOAssetPermission[] {
    if (!this.cryptographyRegistry.isConfigured()) return [];
    return this.repositories.permissions.findAll();
  }

  getPermissionsByAssetId(assetId: AssetId): RTWOAssetPermission[] {
    if (!this.cryptographyRegistry.isConfigured()) return [];
    return this.repositories.permissions.findByAssetId(assetId);
  }

  getPermission(permissionId: PermissionId): RTWOAssetPermission | null {
    if (!this.cryptographyRegistry.isConfigured()) return null;
    return this.repositories.permissions.findById(permissionId);
  }

  getActivePermissionCount(): 0 {
    return 0;
  }
}
